import React, { useContext, useState } from "react";
import { useEffect } from "react";
import {
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
  Button,
} from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Context } from "../../Shared/Context/GlobalContext";
import { usePaymentMethods } from "../../Shared/Hooks/usePaymentMethods";
import { paymentInterval } from "../../Shared/Hooks/useProcessingPaymentInterval";
import { useShippingMethods } from "../../Shared/Hooks/useShippingMethods";
import LoginPage from "../LoginPage";
import { CartListComponent } from "./components/CardItemComponent";
import { CashExternalButtonComponent } from "./PaymentPageComponents/CashExternalButtonComponent";
import { RedsysRedirectionButtonComponent } from "./PaymentPageComponents/RedsysRedirectionButtonComponent";
import es from "date-fns/locale/es";
import { addDays } from "date-fns";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { Woo } from "../../Helpers/Woo";
import { useNavigate } from "react-router";
import routes from "../../Helpers/Routes.json";
import { formatCurrency } from "../../Shared/helpers/currency";
import { ShippingAddressSelector } from "./components/ShippingAddressSelector";
import { ShippingAddressModal } from "./components/ShippingAddressModal";
import { useCustomerAddresses } from "../../Shared/Hooks/useCustomerAddresses";

registerLocale("es", es);

function OrderPage() {
  const { cartProducts, user, resetCart, updateCart } = useContext(Context);
  const { paymentMethods } = usePaymentMethods([]);
  const { shippingMethods } = useShippingMethods([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [shippingMethod, setShippingMethod] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const {
    addresses,
    loading: addressesLoading,
    error: addressesError,
    addAddress,
    deleteAddress,
    selectedAddress,
    selectAddress,
    clearAddress,
  } = useCustomerAddresses();

  const defaultCouponState = {
    form: "",
    applied: false,
    loading: false,
  };
  const [couponCode, setCouponCode] = useState(defaultCouponState);
  const [coupon, setCoupon] = useState(0);
  const [displayCuponForm, setDisplayCouponForm] = useState(false);
  const [comments, setComments] = useState("");

  const { t } = useTranslation();
  const navigate = useNavigate();

  const days = [
    t("Dom"), t("Lun"), t("Mar"), t("Mier"), t("Ju"), t("Vie"), t("Sab"),
  ];
  const months = [
    t("Enero"), t("Febrero"), t("Marzo"), t("Abril"), t("Maryo"),
    t("Junio"), t("Julio"), t("Agosto"), t("Septiembre"), t("Octubre"),
    t("Noviembre"), t("Diciembre"),
  ];

  useEffect(() => {
    clearTimeout(paymentInterval);
    return () => {
      clearTimeout(paymentInterval);
    };
  }, []);

  function validateOrder() {
    if (!shippingMethod) return "missing_shipping_method";
    if (shippingMethod.reservationTimes && !getDeliveryDate())
      return "missing_shipping_delivery_at";
    if (shippingMethod.type === 'delivery' && !selectedAddress)
      return "missing_shipping_address";
    return false;
  }

  function handleCouponSubmit(e) {
    setCouponCode({ ...couponCode, loading: true });
    Woo.coupons
      .get(couponCode.form)
      .then(({ data }) => {
        if (!data) {
          setCouponCode(defaultCouponState);
          e.target.focus();
        } else {
          setCoupon({ ...data, amount: parseFloat(data.amount) });
          setCouponCode({ ...couponCode, applied: true, loading: false });
        }
      })
      .catch((e) => {
        console.warn(e);
        setCouponCode(defaultCouponState);
      });
  }

  let price = cartProducts.reduce((carry, item) => {
    if (item.product.type === "bundle" || item.product.type === "grouped") {
      return (
        carry +
        item.product.bundle.reduce((c, i) => {
          if ("variable" === i.product.type) {
            return (
              c +
              parseFloat(i.product.variation.regular_price) *
                parseInt(item.product.quantity)
            );
          } else {
            return (
              c + parseFloat(i.product.price) * parseInt(item.product.quantity)
            );
          }
        }, 0)
      );
    }
    return (
      parseFloat(item.product.price).toFixed(2) *
        parseInt(item.product.quantity) +
      carry
    );
  }, 0);

  const price_with_vat = price;

  if (!user) {
    return <LoginPage doNavigate={false} />;
  }

  function getDeliveryDate() {
    if (!time || !date) return null;
    return (
      date.toLocaleDateString(i18next.language, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      " " +
      time
    );
  }

  if (coupon) {
    if (coupon.discount_type === "percent") {
      price = parseFloat(
        price - price * (parseInt(coupon.amount) / 100)
      ).toFixed(2);
    } else {
      price = formatCurrency(price - coupon.amount);
    }
  }

  return (
    <Container className="py-4" style={{ maxWidth: "800px" }}>
      <h1 className="h3 fw-bold mb-4">{t("Tu pedido")}</h1>

      {cartProducts.length ? (
        <>
          <CartListComponent
            cartProducts={cartProducts}
            updateCart={updateCart}
          />

          <div className="card p-3 mb-3">
            <div className="d-flex justify-content-between small mb-1">
              <span>{t("Total sin IVA:")}</span>
              <span>
                {formatCurrency(price_with_vat - parseFloat(price_with_vat * 0.1))}
              </span>
            </div>
            <div className="d-flex justify-content-between fw-bold">
              <span>{t("Total con IVA")}:</span>
              <span>{formatCurrency(parseFloat(price_with_vat))}</span>
            </div>

            {couponCode.applied ? (
              <div className="d-flex justify-content-between align-items-center mt-2 small">
                <span>
                  {t("Cupón:")} <em>{couponCode.form}</em>
                  {coupon.discount_type === "percent"
                    ? ` (${coupon.amount}%)`
                    : null}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger p-0"
                  onClick={() => {
                    setCouponCode(defaultCouponState);
                    setCoupon(null);
                    setDisplayCouponForm(false);
                  }}
                >
                  {t("Eliminar")}
                </Button>
              </div>
            ) : (
              <div className="mt-2">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setDisplayCouponForm(!displayCuponForm)}
                >
                  {t("¿Tienes un cupón?")}
                </Button>
                {displayCuponForm && (
                  <div className="d-flex gap-2 mt-2">
                    <Form.Control
                      size="sm"
                      value={couponCode.form}
                      onChange={(e) =>
                        setCouponCode({ ...couponCode, form: e.target.value })
                      }
                      placeholder={t("Introduce el cupón")}
                    />
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={handleCouponSubmit}
                      disabled={
                        couponCode.loading ||
                        !couponCode.form ||
                        couponCode.form.length === 0
                      }
                    >
                      {couponCode.loading ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        t("Aplicar")
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <hr />
            <div className="d-flex justify-content-between h5 mb-0">
              <span>{t("Total del pedido")}:</span>
              <span className="fw-bold">
                {formatCurrency(Math.max(parseFloat(price), 0))}
              </span>
            </div>
          </div>

          <div className="card p-3 mb-4">
            <Form.Label htmlFor="comments-input" className="fw-bold small">
              {t("Alergias o comentarios")}:
            </Form.Label>
            <InputGroup>
              <Form.Control
                as="textarea"
                id="comments-input"
                rows={2}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </InputGroup>
          </div>

          <h2 className="h5 fw-bold mb-3">{t("Forma de entrega")}</h2>
          <Row className="g-2 mb-4">
            {shippingMethods.map(
              ({
                id,
                name,
                description,
                type,
                reservationTimes,
                reservationIntervalTime,
                exceptions = [],
              }) => {
                return (
                  <Col key={`shipping-${id}`} xs={12}>
                      <div
                        role="button"
                        className={`card border p-3 border-2 overflow-visible ${
                          shippingMethod && name === shippingMethod.name
                            ? "border-success bg-light"
                            : ""
                        }`}
                        onClick={() => {
                          setShippingMethod({ name, reservationTimes, type });
                        }}
                      >
                        {reservationTimes ? (
                          <>
                            <h3 className="h6 fw-bold">{name}</h3>
                            <p className="small text-muted mb-2">
                              {description}
                            </p>
                            {shippingMethod?.type === 'delivery' && shippingMethod?.name === name && (
                              <div className="mb-3">
                                <ShippingAddressSelector
                                  selectedAddress={selectedAddress}
                                  onClick={() => setShowAddressModal(true)}
                                  onClear={clearAddress}
                                />
                              </div>
                            )}
                            <DatePicker
                              customInput={
                                <input
                                  className="form-control w-100"
                                  inputMode="none"
                                />
                              }
                              placeholderText={t("Selecciona una fecha")}
                            locale={{
                              localize: {
                                day: (n) => days[n],
                                month: (n) => months[n],
                              },
                              formatLong: {
                                date: () => "dd/MM/yyyy",
                              },
                            }}
                            selected={date}
                            timeIntervals={
                              reservationIntervalTime || 15
                            }
                            timeFormat="HH:mm"
                            onChange={(date) => {
                              setDate(date);
                              setTime(null);
                                setShippingMethod({ name, reservationTimes, type });
                            }}
                            minDate={addDays(new Date(), 1)}
                            maxDate={addDays(new Date(), 15)}
                            dateFormat="dd/MM/yyyy"
                            filterTime={(time) => {
                              const currentDate = new Date();
                              const selectedDate = new Date(time);
                              if (currentDate.getTime() < selectedDate.getTime()) {
                                const weekday = selectedDate
                                  .toLocaleDateString("en-EN", {
                                    weekday: "long",
                                  })
                                  .toLowerCase();
                                let hour = selectedDate.toLocaleTimeString(
                                  "en-EN",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                  }
                                );
                                return reservationTimes[weekday].includes(hour);
                              }
                              return false;
                            }}
                            calendarStartDay={1}
                            excludeDates={exceptions.map((i) => new Date(i))}
                          />
                          <CustomTimeInput
                            date={date}
                            reservationTimes={reservationTimes}
                            setTime={setTime}
                          />
                          {getDeliveryDate() && (
                            <p className="small mt-2 mb-0">
                              <strong>{t("Fecha Entrega")}:</strong>{" "}
                              {getDeliveryDate()}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h3 className="h6 fw-bold mb-1">{name}</h3>
                            <p className="small text-muted mb-0">
                              {description}
                            </p>
                          </div>
                          {shippingMethod && name === shippingMethod.name && (
                            <span className="badge bg-success rounded-pill">✓</span>
                          )}
                        </div>
                      )}
                      {shippingMethod?.type === 'delivery' && shippingMethod?.name === name && !reservationTimes && (
                        <div className="mt-3 pt-3 border-top">
                          <ShippingAddressSelector
                            selectedAddress={selectedAddress}
                            onClick={() => setShowAddressModal(true)}
                            onClear={clearAddress}
                          />
                        </div>
                      )}
                    </div>
                  </Col>
                );
              }
            )}
          </Row>

          <h2 className="h5 fw-bold mb-3">{t("Forma de pago")}</h2>
          <div className="pb-5">
            {price <= 0.5 ? (
              <Button
                variant="dark"
                size="lg"
                className="rounded-pill w-100"
                onClick={(e) => {
                  const validationResult = validateOrder();
                  switch (validationResult) {
                    case "missing_shipping_method":
                      alert(t("Falta seleccionar forma de entrega"));
                      return false;
                    case "missing_shipping_delivery_at":
                      alert(t("Selecciona una fecha de entrega"));
                      return false;
                    case "missing_shipping_address":
                      alert(t("Selecciona una dirección de envío"));
                      return false;
                    default:
                  }
                  fetch(process.env.REACT_APP_API_HOST + "/api/payment", {
                    method: "post",
                    headers: {
                      "X-Customer-Token": user.access_token,
                    },
                    body: JSON.stringify({
                      products: cartProducts,
                      coupon_code: couponCode.applied ? couponCode.form : null,
                      shipping_method: shippingMethod,
                      delivery_at: getDeliveryDate(),
                      comments: comments,
                      shipping_address: selectedAddress,
                    }),
                  })
                    .then((res) => {
                      if (res.status === 200) return res.json();
                      throw new Error("Something went wrong");
                    })
                    .catch((error) => console.error(error))
                    .then(({ data }) => {
                      resetCart();
                      navigate(
                        routes.Payment.success.replace(
                          ":transaction",
                          data.transaction
                        )
                      );
                    });
                }}
              >
                {t("Completar pedido")}
              </Button>
            ) : (
              paymentMethods.map((paymentMethod) => {
                switch (paymentMethod.slug) {
                  case "no-payment-required":
                    return (
                      <CashExternalButtonComponent
                        key={`method-${paymentMethod.id}`}
                        {...paymentMethod}
                        shippingMethod={
                          shippingMethod ? shippingMethod.name : ""
                        }
                        deliveryAt={getDeliveryDate()}
                        validateOrder={validateOrder}
                        cartProducts={cartProducts}
                        couponCode={couponCode.applied ? couponCode.form : null}
                        comments={comments}
                        user={user}
                        shippingAddress={selectedAddress}
                      />
                    );
                  case "redsys-redirection":
                    return (
                      <RedsysRedirectionButtonComponent
                        key={`method-${paymentMethod.id}`}
                        {...paymentMethod}
                        shippingMethod={
                          shippingMethod ? shippingMethod.name : ""
                        }
                        deliveryAt={getDeliveryDate()}
                        validateOrder={validateOrder}
                        cartProducts={cartProducts}
                        couponCode={couponCode.applied ? couponCode.form : null}
                        comments={comments}
                        user={user}
                        shippingAddress={selectedAddress}
                      />
                    );
                  default:
                    return null;
                }
              })
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted mb-4">{t("Tu carrito está vacío.")}</p>
          <Button
            variant="outline-dark"
            className="rounded-pill"
            onClick={() => navigate(routes.Homepage.index)}
          >
            {t("Ver productos")}
          </Button>
        </div>
      )}

      <ShippingAddressModal
        show={showAddressModal}
        setShow={setShowAddressModal}
        addresses={addresses}
        loading={addressesLoading}
        error={addressesError}
        onAdd={addAddress}
        onDelete={deleteAddress}
        selectedAddress={selectedAddress}
        onSelect={(addr) => {
          selectAddress(addr);
          setShowAddressModal(false);
        }}
      />
    </Container>
  );
}

function CustomTimeInput({ date, reservationTimes, setTime }) {
  const { t } = useTranslation();

  if (!date) return null;

  const weekday = new Date(date)
    .toLocaleDateString("en-EN", { weekday: "long" })
    .toLowerCase();

  if (date && reservationTimes[weekday].length) {
    let reservationFiltered = reservationTimes[weekday].filter((hour) => {
      const scheduleDate = new Date(
        date.toLocaleDateString("en-EN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }) +
          " " +
          hour
      );
      return new Date() < scheduleDate;
    });

    if (reservationFiltered.length) {
      return (
        <select
          className="form-select form-select-sm"
          name="time-input"
          onChange={(e) => {
            setTime(e.target.value === "" ? null : e.target.value);
          }}
        >
          <option value="">{t("Selecciona una hora")}</option>
          {reservationFiltered.map((hour) => (
            <option key={`${weekday}-${hour}`} value={hour}>
              {hour}
            </option>
          ))}
        </select>
      );
    }
  }

  return (
    <small className="text-muted">
      {t("No hay horario disponible para esa fecha, prueba otra.")}
    </small>
  );
}

export default OrderPage;
